import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Article } from "@/lib/types";
import { timeAgo } from "@/lib/api";
import { useCardTx, useLang, useArticlePath } from "@/lib/i18n";
import { sectorTrack } from "./SectorHero";

const ctxLabel = (a: Article) => (a as any).context_label || "DeFi";

const LeadCard = ({ article }: { article: Article }) => {
  const { t } = useLang();
  const tx = useCardTx(article.slug);
  const toArticle = useArticlePath();
  const title = tx?.title || article.title;
  const dek = tx?.summary || (article as any).display_dek || article.summary_ai || article.summary || "";
  const img = (article as any).lead_visual?.url || article.hero_image_url;
  return (
    <Link to={toArticle(article.slug)} data-testid="defi-lead-story"
      onClick={() => sectorTrack("lead_click", article.slug, "defi")}
      className="group relative flex flex-col md:flex-row rounded-xl overflow-hidden border border-white/[0.08] bg-[#0b0c10] hover:border-[#34D399]/40 transition-colors">
      <div className="relative aspect-[16/9] md:aspect-auto md:w-[46%] md:min-h-[320px] shrink-0 overflow-hidden bg-[#0e0f16]">
        {img && (
          <img src={img} alt="" fetchPriority="high"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0b0c10]/35 max-md:bg-gradient-to-t max-md:to-[#0b0c10]/50" />
        {article.is_breaking && (
          <span className="absolute top-3 left-3 rounded bg-[#DC2626] px-2 py-1 font-heading font-bold text-[10px] tracking-[0.2em] uppercase text-white">
            {t("breaking")}
          </span>
        )}
      </div>
      <div className="flex flex-col p-5 sm:p-7 md:w-[54%]">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] flex items-center gap-2.5">
          <span className="text-[#34D399] font-bold">{ctxLabel(article)}</span>
          {article.read_minutes ? <span className="text-gray-500">{article.read_minutes} {t("minRead")}</span> : null}
        </p>
        <h2 className="font-news font-black text-white text-[24px] sm:text-[28px] xl:text-[31px] leading-[1.14] tracking-tight mt-3 line-clamp-4 group-hover:text-white">
          {title}
        </h2>
        {dek && (
          <p className="text-gray-400 mt-3 leading-relaxed line-clamp-3 text-sm sm:text-[15px]">{dek}</p>
        )}
        <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-gray-500 mt-auto pt-5">
          {article.author_name ? `${/\bby\b/i.test(article.author_name) ? "" : "By "}${article.author_name}  ·  ` : ""}{timeAgo(article.published_at)}
        </p>
        <span className="mt-3.5 inline-flex items-center gap-2 w-fit rounded-lg border border-[#7C3AED]/60 bg-[#7C3AED]/[0.07] text-white font-semibold text-sm px-4 py-2 group-hover:border-[#7C3AED] group-hover:bg-[#7C3AED]/[0.16] transition-colors">
          Read full story
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </div>
    </Link>
  );
};

const SecondaryCard = ({ article }: { article: Article }) => {
  const tx = useCardTx(article.slug);
  const toArticle = useArticlePath();
  const img = (article as any).lead_visual?.url || article.hero_image_url;
  return (
    <Link to={toArticle(article.slug)} data-testid={`defi-secondary-${article.slug}`}
      onClick={() => sectorTrack("secondary_story_click", article.slug, "defi")}
      className="group flex gap-4 rounded-xl border border-white/[0.08] bg-[#0b0c10] p-4 hover:border-[#34D399]/35 transition-colors flex-1">
      <div className="relative h-[74px] w-[74px] shrink-0 rounded-lg overflow-hidden bg-[#12131a]">
        {img && <img src={img} alt="" loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />}
      </div>
      <div className="min-w-0 flex flex-col">
        <p className="font-mono text-[9.5px] font-bold uppercase tracking-[0.2em] text-[#34D399]">
          {ctxLabel(article)}
        </p>
        <h3 className="font-news font-bold text-white text-[15.5px] leading-snug mt-1.5 line-clamp-2 group-hover:text-[#34D399] transition-colors">
          {tx?.title || article.title}
        </h3>
        <p className="font-mono text-[9.5px] uppercase tracking-wide text-gray-500 mt-auto pt-1.5">
          {timeAgo(article.published_at)}
        </p>
      </div>
    </Link>
  );
};

export const DeFiFrontLayer = ({ lead, secondary }: { lead: Article | null; secondary: Article[] }) => {
  if (!lead) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-6 pb-2" data-testid="defi-front-layer">
      <div className="flex items-center gap-2.5 mb-4">
        <span aria-hidden className="h-3.5 w-[3px] rounded-full bg-[#34D399]" />
        <h2 className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-[#34D399]">DeFi Front Layer</h2>
      </div>
      <div className={`grid gap-5 ${secondary.length ? "lg:grid-cols-[minmax(0,1fr)_minmax(0,390px)] xl:grid-cols-[minmax(0,1fr)_420px]" : ""}`}>
        <LeadCard article={lead} />
        {secondary.length > 0 && (
          <div className="flex flex-col gap-4" data-testid="defi-secondary-stack">
            {secondary.map((a) => <SecondaryCard key={a.slug} article={a} />)}
          </div>
        )}
      </div>
    </section>
  );
};
